import { useNavigate, useParams } from '@tanstack/react-router';
import type React from 'react';
import type { ReactNode } from 'react';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import InfoTooltip from '~/components2/InfoTooltip';
import { MIN_WIDTH } from '~/components2/Splitter';
import type { ProjectDetailsParams } from '../..';

interface DrawerProps {
  title: ReactNode;
  tooltip?: ReactNode;
  children?: ReactNode;
}

export default function Drawer({ title, tooltip, children }: DrawerProps) {
  const { projectId, tabId } = useParams({ strict: false }) as ProjectDetailsParams;
  const navigate = useNavigate();

  return (
    <div
      className="absolute right-0 top-0 z-[20] flex h-full flex-col justify-between space-y-4 overflow-hidden border-l bg-white py-4 "
      style={{ width: MIN_WIDTH }}
    >
      <div className="flex flex-col space-y-4 px-4">
        <div className="flex w-full items-center justify-between">
          <div className="inline-flex items-center space-x-1 text-lg">
            <span data-testid="utilities-drawer-title">{title}</span>
            {tooltip && (
              <InfoTooltip text={tooltip} label={typeof title === 'string' ? `About ${title}` : 'More information'} />
            )}
          </div>
          <CloseIconButton
            aria-label={typeof title === 'string' ? `Close ${title}` : 'Close drawer'}
            data-testid="utilities-drawer-close"
            onClick={() => {
              navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId } });
            }}
          />
        </div>
        <hr />
      </div>

      <div className="relative grow overflow-auto">{children}</div>
    </div>
  );
}

type ChildProps = {
  className?: string;
  children?: React.ReactNode;
};

export const DrawerContainer = ({ className, children }: ChildProps) => {
  return <div className={`flex h-full flex-col justify-between space-y-4 ${className}`}>{children}</div>;
};

export const DrawerContent = ({ className, children }: ChildProps) => {
  return <div className={`grow overflow-y-auto px-4 ${className}`}>{children}</div>;
};

export const DrawerFooter = ({ className, children }: ChildProps) => {
  return <div className={`flex items-center justify-between border-t pt-2 mx-4 ${className}`}>{children}</div>;
};
